import { DownloadOutlined } from '@ant-design/icons'
import type { ViewFieldsFragment } from '@salescore/client-api'
import { useCanForView } from '@salescore/client-common'
import type { CompiledKpiPivotViewConfig, CompiledKpiViewConfig } from '@salescore/core'
import { useStateWithLocalStorage } from '@salescore/frontend-common'
import { Button, Popover, Row, Switch } from 'antd'
import { t } from 'i18next'
import type { ReactElement } from 'react'
import { z } from 'zod'

import { DashboardCsvDownloadButton } from './DashboardCsvDownloadButton'

const CSV_GOAL_INCLUDED_STORAGE_KEY = 'view/navigation/csvGoalIncluded'

export const DashboardCsvDownloadPopover = ({
  compiledConfig,
  view,
}: {
  compiledConfig: CompiledKpiViewConfig | CompiledKpiPivotViewConfig
  view: ViewFieldsFragment
}): ReactElement => {
  const canDownloadCsv = useCanForView('download-csv', view)
  // Redux に置かない: 読み手はこの部品の中のスイッチと CSV 組み立ての 2 箇所だけで、
  // slice・selector・store 登録を足しても誰の挙動も変わらない。複数配置・別タブ間の同期は hook が localStorage の変更イベントで行う。
  // localStorage への書き込みに失敗するとスイッチ自体が切り替わらない (usehooks-ts は setItem 成功後に state を更新する)。
  // 表示と出力が食い違わないので受け入れる。localStorage が使えないブラウザは既存の表示設定の保存も例外になり、元から対象外。
  const [shouldIncludeGoal, setShouldIncludeGoal] = useStateWithLocalStorage(
    CSV_GOAL_INCLUDED_STORAGE_KEY,
    true,
    z.boolean(),
  )
  const trigger = <Button type="text" icon={<DownloadOutlined />} disabled={!canDownloadCsv} />

  if (!canDownloadCsv) {
    return trigger
  }

  // アイコンの押下ではダウンロードさせず、フォームのボタンからだけ行う。
  // 即ダウンロードできると、前回オフにしたことを忘れた利用者がスイッチを見ないまま目標なし CSV を受け取る。
  // antd は既定でフォームを body 直下に描画し、Tab の移動順でアイコンの次に来なくなる。
  // キーボードだけでフォームのボタンまで進めるよう、アイコンを包む span の中に描画する。
  return (
    <span>
      <Popover
        placement="bottomLeft"
        trigger={['hover', 'click']}
        getPopupContainer={(triggerNode) => triggerNode.parentElement ?? document.body}
        content={
          <div style={{ width: 280 }}>
            <Row justify="space-between" align="middle">
              <span className="font-bold">{t('目標を含める')}</span>
              <Switch size="small" checked={shouldIncludeGoal} onChange={setShouldIncludeGoal} />
            </Row>
            <div className="mt-1 text-xs text-gray-500">
              {t(
                'オフにすると、KPIごとの目標列を除いた実績のみのCSVをダウンロードします。設定はこのブラウザに保存されます。',
              )}
            </div>
            <Row justify="end" className="mt-3">
              <DashboardCsvDownloadButton
                compiledConfig={compiledConfig}
                view={view}
                shouldIncludeGoal={shouldIncludeGoal}
              />
            </Row>
          </div>
        }
      >
        {trigger}
      </Popover>
    </span>
  )
}
